import * as React from 'react'
import { ReactElement, useEffect, useState } from 'react'
import { Button, Input, makeStyles, mergeClasses, MessageBar, MessageBarBody, Text } from '@fluentui/react-components'
import { InputProps } from '@fluentui/react-input'
import { TextProps } from '@fluentui/react-text'
import LoadingIcon from '../../../components/Icon/LoadingIcon'
import {
    ArrowHookUpLeftFilled,
    ArrowHookUpLeftRegular,
    bundleIcon,
    RenameFilled,
    RenameRegular,
    SaveFilled,
    SaveRegular,
} from '@fluentui/react-icons'
import { useApplicationStyles } from '../../../pages/App'
import { normalizeAxiosError } from '../../../reducers/errorReducer'

const useStyles = makeStyles({
    text: {
        height: '30px',
        alignContent: 'center',
    },
})

const RenameIcon = bundleIcon(RenameFilled, RenameRegular)
const BackIcon = bundleIcon(ArrowHookUpLeftFilled, ArrowHookUpLeftRegular)
const SaveIcon = bundleIcon(SaveFilled, SaveRegular)

type InlineSelfSubmittingInputProps = {
    value: string //The value to edit.
    onSubmit: (value: string) => Promise<any> //Callback to use when user presses Enter or clicks on Save icon.

    textProps?: TextProps //Props to be applied to the Text component.
    inputProps?: Omit<InputProps, 'value' | 'onChange'> //Props to be applied to the Input component.

    disabled?: boolean //Whether this component should be disabled.
    isEditing?: boolean //State to indicate this component is in Edit mode. Not used internally, but for external use.
    setIsEditing?: (isSubmitting: boolean) => void | Promise<any> //Setter to have component indicate that this component is in Edit mode. Not used internally, but for external use.
    isSubmitting?: boolean //State to indicate this component is Saving. Not used internally, but for external use.
    setIsSubmitting?: (isSubmitting: boolean) => void | Promise<any> //Setter to have component indicate that this component is Saving. Not used internally, but for external use.
}

export default function InlineSelfSubmittingInput(props: InlineSelfSubmittingInputProps): ReactElement {
    const appStyles = useApplicationStyles()
    const styles = useStyles()

    const [value, setValue] = useState(props.value)
    const [previousValue, setPreviousValue] = useState(props.value)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [error, setError] = useState<string>(undefined)

    const submit = async () => {
        if (isSubmitting) {
            return
        }

        try {
            setIsSubmitting(true)
            props.setIsSubmitting?.(true)
            await props.onSubmit(value)

            setIsEditing(false)
            props.setIsEditing?.(false)
            setError(undefined)
            setPreviousValue(value)
        } catch (e) {
            console.error(e)
            const error = normalizeAxiosError(e)
            setError(`${error.code}: ${error.message}`)
        } finally {
            setIsSubmitting(false)
            props.setIsSubmitting?.(false)
        }
    }

    useEffect(() => {
        setIsSubmitting(false)
        props.setIsSubmitting?.(false)
        setIsEditing(false)
        props.setIsEditing?.(false)

        setError(undefined)
        setValue(props.value)
        setPreviousValue(props.value)
    }, [props.value])

    if (isEditing) {
        return (
            <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS, appStyles.flexColumn)}>
                <Input
                    {...props.inputProps}
                    value={value}
                    onChange={(_, data) => setValue(data.value)}
                    contentBefore={
                        isSubmitting ? (
                            <LoadingIcon />
                        ) : (
                            (props.inputProps?.contentBefore ?? (
                                <Button
                                    size={'small'}
                                    appearance={'subtle'}
                                    icon={<BackIcon />}
                                    onClick={() => {
                                        setValue(previousValue)
                                        setError(undefined)

                                        setIsEditing(false)
                                        props.setIsEditing?.(false)
                                    }}
                                />
                            ))
                        )
                    }
                    contentAfter={
                        props.inputProps?.contentAfter ?? (
                            <Button size={'small'} appearance={'subtle'} icon={<SaveIcon />} onClick={async () => submit()} />
                        )
                    }
                    onKeyDown={async e => {
                        if (e.key === 'Enter') {
                            e.preventDefault()
                            await submit()
                        }
                    }}
                />
                {error && (
                    <MessageBar>
                        <MessageBarBody>{error}</MessageBarBody>
                    </MessageBar>
                )}
            </div>
        )
    }

    return (
        <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS)}>
            {!isEditing && (
                <Button
                    size={'small'}
                    appearance={'subtle'}
                    icon={<RenameIcon />}
                    onClick={() => {
                        setPreviousValue(value)

                        setIsEditing(true)
                        props.setIsEditing?.(true)
                    }}
                />
            )}
            <Text
                className={styles.text}
                {...props.textProps}
                onDoubleClick={() => {
                    setPreviousValue(value)

                    setIsEditing(true)
                    props.setIsEditing?.(true)
                }}
            >
                {value}
            </Text>
        </div>
    )
}
